/**
 * Все числа баланса в одном месте (ТЗ §14).
 *
 * Механика: два трона, две армии. Зритель пишет A или B, встаёт в очередь,
 * выходит из-за своего трона и идёт на трон противника.
 *
 * ─── Про то, как эта конструкция вообще заканчивается ────────────────────
 *
 * «Трон защищён, пока жив хоть один защитник» + «выбывший возвращается,
 * написав в чат» = трон недостижим никогда: при живой аудитории сторона не
 * бывает пустой ни секунды. Хуже того, при уроне 1 и сотне HP темп убийств
 * почти равен темпу подкреплений, поэтому горстка живых у обороняющихся
 * остаётся всегда.
 *
 * Поэтому трон открывает не полное истребление армии, а ПРИЖАТАЯ К НЕМУ
 * ЛИНИЯ ФРОНТА. Через нейтральную полосу вокруг линии не проходит никто,
 * стороны перестреливаются через неё, а сама линия ползёт туда, где бойцов
 * меньше. Дойдя до frontMargin от трона, линия встаёт: армия продавлена и
 * стоит у ворот. По смыслу то же самое, но состояние достижимо и читается
 * зрителем с первого взгляда.
 */

export type TeamId = 'A' | 'B';

export type WeaponId = 'volley' | 'laser' | 'rocket' | 'barrage' | 'orbital' | 'purge';

export interface WeaponTier {
  min: number;
  /** null = без верхней границы */
  max: number | null;
  weapon: WeaponId;
  /** Короткая подпись для плашки цен. */
  label: string;
  /**
   * Значок подарка для плашки, пока нет каталога комнаты.
   *
   * Настоящие картинки подарков приходят от коннектора вместе с каталогом
   * (enableExtendedGiftInfo) и подменяют эти заглушки. Захардкоживать ссылки
   * на CDN TikTok нельзя: витрина у разных стримеров разная, а адреса
   * меняются (ТЗ §4.4).
   */
  icon: string;
  /**
   * Картинка оружия в плашке. Слова «ВИНЧЕСТЕР» и «12 РАКЕТ» занимают полосу
   * и всё равно требуют перевода взглядом; силуэт понятен сразу.
   */
  mark: 'rocket' | 'rifle' | 'laser';
}

export interface GameConfig {
  match: {
    lobbySec: number;
    /** 0 = без ограничения по времени. */
    maxDurationSec: number;
    finaleSec: number;
    resultsSec: number;
    finaleTimeScale: number;
  };

  arena: {
    /** Отступ центра трона от края арены. */
    throneOffsetY: number;
    throneRadius: number;

    /**
     * Ширина невидимой нейтральной полосы посреди фронта. Через неё бойцы
     * не проходят — стоят по свою сторону и перестреливаются на расстоянии.
     */
    bufferWidth: number;
    /** Скорость, с которой линия фронта ползёт к отстающей стороне, px/с. */
    frontSpeed: number;
    /**
     * Чувствительность линии к перевесу по боевой мощи. 1 — линия стоит
     * ровно там, где говорит доля мощи, и при близких силах почти не
     * двигается. Больше единицы — небольшой перевес уже заметно гонит
     * фронт вперёд.
     */
    frontResponse: number;
    /**
     * Насколько близко линия подходит к трону и останавливается. Дальше она
     * не смещается: прижатая армия добивается на месте, а трон открыт.
     */
    frontMargin: number;
  };

  throne: {
    hp: number;
    /** Реген в секунду, до потолка hp. */
    regenPerSec: number;
    /** Трон открывается, когда линия фронта прижата к нему ближе этого зазора. */
    openGap: number;
    /** И закрывается обратно, когда линию отжали дальше этого. */
    restoreGap: number;
  };

  unit: {
    baseHp: number;
    hpPerLevel: number;
    maxLevel: number;
    baseRadius: number;
    /** Радиус на максимальном уровне не превышает baseRadius × maxRadiusScale. */
    maxRadiusScale: number;
    damage: number;
    shotIntervalMs: number;
    shotJitterMs: number;
    /** Дальность стрельбы. */
    range: number;
    projectileSpeed: number;
    projectileTtlMs: number;
    speed: number;
    /** Доля бойцов, которые при открытом троне бьют по трону, а не по живым. */
    throneFocus: number;
    /** Боковое рыскание при марше, чтобы строй не был линейкой. */
    driftSpeed: number;
    separationForce: number;
    /** Неуязвимость сразу после выхода из очереди. */
    spawnInvulnMs: number;
    maxProjectiles: number;
    retargetMs: number;
  };

  levels: {
    /** Уровень при смерти сбрасывается до первого. */
    resetOnDeath: boolean;
    /**
     * Сколько добивающих выстрелов нужно на один уровень.
     *
     * При одном киле за уровень десятый брали каждую минуту и корона
     * обесценивалась: в прогоне выходило под два десятка королей за матч.
     */
    killsPerLevel: number;
    /** Прибавка к текущему HP при повышении уровня. */
    healOnLevelUp: number;
  };

  queue: {
    /** Потолок живых на поле, обе стороны вместе. */
    maxAlive: number;
    /**
     * Ограничение темпа впуска из очереди, бойцов в секунду на сторону.
     * 0 — без ограничения: пока на поле есть места, зритель попадает в бой
     * сразу же. Стоять в очереди при полупустой арене бессмысленно, очередь
     * должна возникать только когда мест действительно нет.
     */
    entriesPerSec: number;
    /**
     * Пауза перед возрождением убитого, секунды.
     *
     * Ключевая механика темпа: новый зритель входит в бой сразу, а погибший
     * выбывает на это время. Именно поэтому убийство даёт стороне реальное
     * преимущество — иначе место павшего мгновенно занимал бы следующий,
     * армия не слабела бы никогда и фронт стоял бы колом.
     */
    respawnSec: number;
    /** Сколько шаров очереди видно на экране, остальные счётчиком. */
    visibleSlots: number;
    /** Ограничение размера очереди на сторону. */
    maxQueued: number;
  };

  /**
   * Плотность поля: при росте числа живых бойцы пропорционально мельчают,
   * чтобы и пятьсот помещались, и картинка читалась.
   */
  density: {
    refCount: number;
    minScale: number;
  };

  weapons: {
    tiers: WeaponTier[];
    /**
     * Роза за 1💎 — самый частый подарок в TikTok, поэтому у неё свой залп:
     * россыпь обычных пуль веером в сторону врага. Урон как у рядового
     * выстрела, берёт количеством и зрелищем, а не силой.
     */
    volley: {
      /** Общее число пуль за весь залп. */
      count: number;
      /**
       * На сколько волн он разбит. Сто пуль разом — это одна вспышка и
       * мгновенно опустевший пул снарядов; четыре волны по двадцать пять
       * читаются как очередь и держат экран почти секунду.
       */
      waves: number;
      /** Пауза между волнами. */
      waveGapMs: number;
      damage: number;
      speed: number;
      /** Полуугол разлёта веера, рад. */
      spread: number;
      throneDamage: number;
    };
    laser: { damage: number; width: number; throneDamage: number };
    /**
     * Залп ракет.
     *
     * ВНИМАНИЕ: throneDamage здесь — урон по трону за ВЕСЬ залп, он делится
     * между ракетами. Когда он считался с каждой, подарок за 200💎 снимал
     * 12 × 900 = 10 800 при троне в 10 000, то есть сносил его в одиночку.
     */
    rocket: {
      damage: number;
      radius: number;
      throneDamage: number;
      speed: number;
      count: number;
      /** Скорость доворота на цель, рад/с. */
      turnRate: number;
    };
    barrage: {
      damage: number;
      radius: number;
      count: number;
      throneDamage: number;
      speed: number;
      turnRate: number;
    };
    orbital: { damage: number; radius: number; throneDamage: number };
    /** Верхняя ступень: мгновенно убивает рядовых в области. */
    purge: {
      radius: number;
      /** Убивает бойцов не выше этого уровня. */
      maxLevel: number;
      /** Не более этой доли живых бойцов противника за один удар. */
      maxFraction: number;
      throneDamage: number;
    };
    /** Не более N применений оружия от одного userId за окно (§12). */
    perUserLimit: number;
    perUserWindowMs: number;
  };

  free: {
    /** Любой комментарий чинит свой шар. */
    commentHeal: number;
    commentCooldownMs: number;
    followHeal: number;
    subscribeHeal: number;
    crownSec: number;
    shareCooldownMs: number;
    likeShieldBase: number;
    likeShieldPerViewer: number;
    likeShieldSec: number;
  };

  /** Голос диктора: пороги серий убийств. */
  announce: {
    /** Окно, внутри которого килы считаются одной серией. */
    comboWindowMs: number;
    /** Сколько убийств за одну жизнь до «Rampage» и «Dominating». */
    rampageAt: number;
    dominatingAt: number;
    /** Минимальная пауза между репликами, чтобы диктор не тараторил. */
    minGapMs: number;
  };

  teams: {
    A: TeamConfig;
    B: TeamConfig;
  };
}

export interface TeamConfig {
  label: string;
  color: number;
  colorDark: number;
  /**
   * Картинка внутри трона — лицо команды.
   *
   * Ради неё зритель и выбирает сторону: «Месси или Роналду» читается с
   * первого взгляда, а «A или B» не значит ничего. Пусто — трон рисуется
   * без портрета.
   */
  imageUrl: string;
}

export const defaultConfig: GameConfig = {
  match: {
    lobbySec: 6,
    maxDurationSec: 0,
    finaleSec: 5,
    resultsSec: 20,
    finaleTimeScale: 0.35,
  },

  arena: {
    throneOffsetY: 180,
    throneRadius: 92,
    bufferWidth: 120,
    frontSpeed: 18,
    frontResponse: 1,
    frontMargin: 240,
  },

  throne: {
    hp: 10000,
    regenPerSec: 1,
    openGap: 10,
    restoreGap: 75,
  },

  unit: {
    baseHp: 100,
    hpPerLevel: 100,
    maxLevel: 10,
    baseRadius: 26,
    maxRadiusScale: 3.2,
    damage: 1,
    shotIntervalMs: 700,
    shotJitterMs: 250,
    range: 900,
    projectileSpeed: 720,
    projectileTtlMs: 1600,
    speed: 78,
    throneFocus: 0.45,
    driftSpeed: 26,
    separationForce: 190,
    spawnInvulnMs: 600,
    maxProjectiles: 1400,
    retargetMs: 450,
  },

  levels: {
    resetOnDeath: true,
    killsPerLevel: 3,
    healOnLevelUp: 100,
  },

  queue: {
    maxAlive: 200,
    entriesPerSec: 1,
    respawnSec: 20,
    visibleSlots: 10,
    maxQueued: 400,
  },

  density: {
    refCount: 250,
    minScale: 0.45,
  },

  weapons: {
    tiers: [
      // Верхние ступени (орбитальный удар и «Кара») пока отключены: они
      // слишком сильно решают матч. Само оружие в коде осталось — чтобы
      // вернуть, достаточно дописать сюда строку.
      { min: 1, max: 4, weapon: 'volley', label: '', icon: '🌹', mark: 'rifle' },
      { min: 5, max: 29, weapon: 'laser', label: '', icon: '🫰', mark: 'laser' },
      { min: 30, max: 199, weapon: 'rocket', label: '5', icon: '🍩', mark: 'rocket' },
      // Верхняя ступень открытая: подарок дороже 200💎 не должен пропадать
      // впустую только потому, что для него нет своей строки.
      { min: 200, max: null, weapon: 'barrage', label: '12', icon: '🔫', mark: 'rocket' },
    ],
    volley: { count: 100, waves: 4, waveGapMs: 150, damage: 1, speed: 1150, spread: 0.85, throneDamage: 300 },
    laser: { damage: 45, width: 46, throneDamage: 120 },
    rocket: { damage: 16, radius: 88, throneDamage: 500, speed: 520, count: 5, turnRate: 3.4 },
    barrage: { damage: 18, radius: 88, count: 12, throneDamage: 1200, speed: 520, turnRate: 3.4 },
    orbital: { damage: 100, radius: 330, throneDamage: 2000 },
    purge: { radius: 520, maxLevel: 1, maxFraction: 1 / 3, throneDamage: 3000 },
    perUserLimit: 6,
    perUserWindowMs: 60000,
  },

  free: {
    commentHeal: 5,
    commentCooldownMs: 10000,
    followHeal: 50,
    subscribeHeal: 50,
    crownSec: 30,
    shareCooldownMs: 180000,
    likeShieldBase: 500,
    likeShieldPerViewer: 8,
    likeShieldSec: 6,
  },

  announce: {
    comboWindowMs: 4000,
    rampageAt: 5,
    dominatingAt: 9,
    minGapMs: 1400,
  },

  teams: {
    // Тема по умолчанию. Меняется ссылкой: ?a=…&b=…&aimg=…&bimg=…
    // Сторона A всегда красная и сверху, B — синяя и снизу: цвет закреплён
    // за буквой, иначе зритель не поймёт, за кого он пишет.
    A: { label: 'РОНАЛДУ', color: 0xff4d5a, colorDark: 0x2a0f14, imageUrl: '/teams/a.png' },
    B: { label: 'МЕССИ', color: 0x4da3ff, colorDark: 0x0d1a2a, imageUrl: '/teams/b.png' },
  },
};

/** Ступень по числу диамантов. Считается по СУММЕ события, а не по одному подарку. */
export function weaponFor(cfg: GameConfig, diamonds: number): WeaponTier | null {
  for (const t of cfg.weapons.tiers) {
    if (diamonds >= t.min && (t.max === null || diamonds <= t.max)) return t;
  }
  return null;
}

/** Максимум HP на уровне: 1 → 100, 10 → 1000. */
export function hpForLevel(cfg: GameConfig, level: number): number {
  return cfg.unit.baseHp + cfg.unit.hpPerLevel * (level - 1);
}

/**
 * Радиус бойца. Площадь шара пропорциональна запасу HP — размер на экране
 * честно показывает, сколько в бойце здоровья, поэтому радиус растёт как
 * корень уровня, а не линейно (иначе десятый уровень занимал бы четверть
 * экрана в поперечнике).
 */
export function radiusForLevel(cfg: GameConfig, level: number, densityScale: number): number {
  const k = Math.min(Math.sqrt(level), cfg.unit.maxRadiusScale);
  return cfg.unit.baseRadius * k * densityScale;
}

/** Общий масштаб поля: чем больше живых, тем мельче бойцы. */
export function densityScaleFor(cfg: GameConfig, aliveCount: number): number {
  if (aliveCount <= cfg.density.refCount) return 1;
  return Math.max(cfg.density.minScale, Math.sqrt(cfg.density.refCount / aliveCount));
}

/** Цвет по уровню — используется в подписях и итогах. */
export const LEVEL_COLORS = [
  0x8b98b0, 0x7fd8ff, 0x5ce1a6, 0x9ae05c, 0xe0d95c, 0xffb03a, 0xff7a2f, 0xff4d5a, 0xd45cff,
  0xffd25e,
] as const;

export function levelColor(level: number): number {
  return LEVEL_COLORS[Math.max(0, Math.min(LEVEL_COLORS.length - 1, level - 1))]!;
}

/** Глубокое слияние частичного конфига — для горячей правки чисел из дев-панели. */
export function mergeConfig(base: GameConfig, patch: DeepPartial<GameConfig>): GameConfig {
  const out: Record<string, unknown> = { ...base };
  for (const [k, v] of Object.entries(patch ?? {})) {
    if (v === undefined) continue;
    const cur = (base as unknown as Record<string, unknown>)[k];
    out[k] =
      v && typeof v === 'object' && !Array.isArray(v) && cur
        ? mergeConfig(cur as GameConfig, v as DeepPartial<GameConfig>)
        : v;
  }
  return out as unknown as GameConfig;
}

export type DeepPartial<T> = {
  [K in keyof T]?: T[K] extends object ? DeepPartial<T[K]> : T[K];
};
